import { useCallback, useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import {
  Dropdown,
  DropdownChip,
  DropdownContent,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { L3_PATHS } from 'Deer/constants/routes';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  setAllTimelines,
  setTimelinesData,
  updateSelectedTimelines,
  setOwnership,
  useOKROwnership,
} from 'reducers/okrsPageSlice';
import { getTimeBoundsFromLS } from 'screens/Initiatives/helpers';
import { useOKRGroupByViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRGroupByViewType';
import { useOKRManageViewType } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useOKRManageViewType';
import { useGoalAnalytics } from 'screens/analytics/goals/GoalAnalyticsContext';
import { selectOrgData } from 'selectors/orgData';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { isTimeBoundPeriodValid } from 'constants/helper';
import { localStorageKeys } from 'constants/localStorageKeys';

import { getTimeBoundTimePeriods } from 'services/admin';

import { MultiSelectWithCheckboxes } from 'uiToolkitV1/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  setSelectedLocations,
  setSelectedCompanies,
  useAllGoalsLocations,
  useAllGoalsCompanies,
  useSelectedGoalsLocations,
  useSelectedGoalsCompanies,
  useSelectGoalsSelectedIntent,
} from '../slice/GoalsIntentSlice';
import '../styles/data-for.css';
import { GoalsIntentTimelineDropdown } from './GoalsIntentTimelineDropdown';

export const setTimeBoundsToLS = (timeBounds) => {
  try {
    window.localStorage.setItem(localStorageKeys.teamPageGoalsTimeBounds, JSON.stringify(timeBounds));
  } catch (e) {
    console.error('Error in saving to LS: ', e);
  }
};

const layoutViewNameMapping = (label) => {
  switch (label) {
    case 'List with hierarchy':
      return 'Hierarchical view';
    case 'Flat view':
      return 'Hierarchical view';
    case 'Tree':
      return 'Tree view';
    default:
      return label;
  }
};

// eslint-disable-next-line no-shadow
export enum OWNERSHIP_TYPE {
  OWNER = 'OWNER',
  OWNER_AND_COLLABORATOR = 'OWNER_AND_COLLABORATOR',
}

export const useIsCompanyGoalsManagePage = () => {
  const location = useLocation();
  return location.pathname.startsWith(L3_PATHS.company.goal.manage);
};

const MetaFilters = ({ disableMetaFilters }: { disableMetaFilters: boolean }) => {
  const { updateTimeBoundPeriods } = useGoalAnalytics();
  const [outsideClickToggle, setOutsideClickToggle] = useState(false);

  const dispatch = useDispatch();

  const { includeCollaboratorGoals } = useSelector(selectOrgData, shallowEqual); // initial value

  const [ownershipFilterValue, setOwnershipFilterValue] = useState(null);

  const ownership = useOKROwnership() as OWNERSHIP_TYPE | null;

  const isCompanyGoalsManagePage = useIsCompanyGoalsManagePage();
  const allCompanies = useAllGoalsCompanies();
  const selectedCompanies = useSelectedGoalsCompanies();
  const selectedLocations = useSelectedGoalsLocations();
  const allLocations = useAllGoalsLocations();

  const showCompanyFilter = checkEnableCompanySupport() && isCompanyGoalsManagePage && allCompanies.length > 1;
  const showLocationFilter = checkEnableCompanySupport() && isCompanyGoalsManagePage && allLocations.length > 1;

  useEffect(() => {
    if (ownership !== null) {
      setOwnershipFilterValue(ownership);
    } else {
      dispatch(setOwnership(includeCollaboratorGoals ? OWNERSHIP_TYPE.OWNER_AND_COLLABORATOR : OWNERSHIP_TYPE.OWNER));
    }
  }, [dispatch, includeCollaboratorGoals, ownership]);

  // timeline data
  // TODO: fix this, very old
  const getTimePeriodsData = useCallback(async () => {
    try {
      const timePeriodData = await getTimeBoundTimePeriods();

      const { previous, current, next, other } = timePeriodData;
      const allTimePeriods = [previous, current, next, ...other].filter((item) => item !== null);

      batch(() => {
        dispatch(setTimelinesData(timePeriodData));
        dispatch(setAllTimelines(allTimePeriods));
      });

      const timeBoundPeriodsFromLS = getTimeBoundsFromLS();
      // read from LS and check if it's still valid from latest fetched timeBound list
      if (
        Array.isArray(timeBoundPeriodsFromLS) &&
        timeBoundPeriodsFromLS.length > 0 &&
        isTimeBoundPeriodValid(allTimePeriods, timeBoundPeriodsFromLS[0])
      ) {
        dispatch(updateSelectedTimelines(timeBoundPeriodsFromLS));
        updateTimeBoundPeriods(timeBoundPeriodsFromLS);
      } else {
        dispatch(updateSelectedTimelines([current]));
        updateTimeBoundPeriods(current);
        setTimeBoundsToLS([current]);
      }
    } catch (error) {
      // dispatch(showErrorSnackbar(error.message));
    }
  }, [updateTimeBoundPeriods, dispatch]);

  useEffect(() => {
    getTimePeriodsData();
  }, [getTimePeriodsData]);

  const { viewType: OKRViewType } = useOKRManageViewType();

  const {
    applicableGroupByValues,
    showGroupByDropdown,
    applicableOKRViewValues,
    showOKRViewDropdown,
    showTimelineDropdown,
    showGoalOwnershipFilter,
    timelineDropdownOnChangeCallback,
    ownershipDropdownOnChangeCallback,
  } = useSelectGoalsSelectedIntent() ?? {};

  // const { viewType: selectedGroupBy } = useOKRInsights();
  const { groupByViewType, setGroupByViewType } = useOKRGroupByViewType();

  const selectedGroupByLabel = applicableGroupByValues?.find((option) => option.value === groupByViewType)?.label;

  const selectedOKRViewLabel = layoutViewNameMapping(
    applicableOKRViewValues?.find((option) => option.value === OKRViewType)?.label
  );

  const SelectedOKRViewIcon = applicableOKRViewValues?.find((option) => option.value === OKRViewType)?.Icon;

  const onGroupByChange = useCallback(
    (value: 'OBJECTIVE' | 'INDIVIDUAL') => {
      // dispatch(setViewType(value));

      setGroupByViewType(value);
      // if (value === 'INDIVIDUAL') {
      //   replaceOKRViewType('list');
      // }
    },
    [
      // replaceOKRViewType,
      setGroupByViewType,
    ]
  );

  const onOwnershipChange = useCallback(
    (value: OWNERSHIP_TYPE) => {
      dispatch(setOwnership(value));
    },
    [dispatch]
  );

  useEffect(() => {
    if (
      !showGroupByDropdown &&
      applicableGroupByValues?.length > 1 &&
      groupByViewType !== applicableGroupByValues?.[0].value
    ) {
      onGroupByChange(applicableGroupByValues?.[0].value as 'OBJECTIVE' | 'INDIVIDUAL');
    }
  }, [applicableGroupByValues, groupByViewType, onGroupByChange, showGroupByDropdown]);

  // const onDataForChange = (value: string) => {
  //   history.push(value);
  // };

  const disableStyleProps = disableMetaFilters ? { pointerEvents: 'none', opacity: 0.4 } : {};

  return (
    <Flex gap="12px" {...disableStyleProps} flexWrap="wrap">
      {showOKRViewDropdown && (
        <Box
          display="flex"
          onPointerDownCapture={() => {
            setOutsideClickToggle(!outsideClickToggle);
          }}
        >
          <Dropdown>
            <DropdownTrigger asChild>
              <DropdownChip className="chip-dropdown">
                <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
                  Layout :
                </TextBodyMedium>
                <Box display="inline-flex" alignItems="center" justifyContent="center" gap="4px">
                  {SelectedOKRViewIcon && <SelectedOKRViewIcon />}
                  <TextBodyMedium bold color="text.g1">
                    {selectedOKRViewLabel ?? ''}
                  </TextBodyMedium>
                </Box>
              </DropdownChip>
            </DropdownTrigger>
            <DropdownContent align="start" style={{ minWidth: '200px' }}>
              <DropdownRadioGroup value={OKRViewType}>
                {applicableOKRViewValues?.map(({ label, value, onClick, Icon }) => (
                  <DropdownRadioItem key={value} value={value} onClick={onClick}>
                    <Box display="inline-flex" alignItems="center" justifyContent="center" gap="4px">
                      <Icon boxSize="24px" />
                      {layoutViewNameMapping(label)}
                    </Box>
                  </DropdownRadioItem>
                ))}
              </DropdownRadioGroup>
            </DropdownContent>
          </Dropdown>
        </Box>
      )}
      {showGroupByDropdown && (
        <Box
          display="flex"
          onPointerDownCapture={() => {
            setOutsideClickToggle(!outsideClickToggle);
          }}
        >
          <Dropdown>
            <DropdownTrigger asChild>
              <DropdownChip className="chip-dropdown">
                <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
                  Group by :
                </TextBodyMedium>
                <TextBodyMedium bold color="text.g1">
                  {selectedGroupByLabel ?? ''}
                </TextBodyMedium>
              </DropdownChip>
            </DropdownTrigger>
            <DropdownContent align="start" style={{ minWidth: '200px' }}>
              <DropdownRadioGroup value={groupByViewType}>
                {applicableGroupByValues?.map(({ label, value }) => (
                  <DropdownRadioItem key={value} value={value} onClick={() => onGroupByChange(value)}>
                    {label}
                  </DropdownRadioItem>
                ))}
              </DropdownRadioGroup>
            </DropdownContent>
          </Dropdown>
        </Box>
      )}
      {showTimelineDropdown && (
        <Box
          display="flex"
          onPointerDownCapture={() => {
            setOutsideClickToggle(!outsideClickToggle);
          }}
        >
          <GoalsIntentTimelineDropdown timelineDropdownOnChangeCallback={timelineDropdownOnChangeCallback} />
        </Box>
      )}
      {showGoalOwnershipFilter && (
        <Box
          display="flex"
          onPointerDownCapture={() => {
            setOutsideClickToggle(!outsideClickToggle);
          }}
        >
          <Dropdown>
            <DropdownTrigger asChild>
              <DropdownChip className="chip-dropdown">
                <TextBodyMedium bold color="text.g2" className="group-data[state=open]:text-[#004F67]">
                  Ownership :
                </TextBodyMedium>
                <TextBodyMedium bold color="text.g1">
                  {ownershipFilterValue === OWNERSHIP_TYPE.OWNER ? 'Owner only' : 'Owner or Collaborator'}
                </TextBodyMedium>
              </DropdownChip>
            </DropdownTrigger>
            <DropdownContent align="start" style={{ minWidth: '200px' }}>
              <DropdownRadioGroup value={groupByViewType}>
                <DropdownRadioItem
                  value={OWNERSHIP_TYPE.OWNER}
                  onClick={() => {
                    if (typeof ownershipDropdownOnChangeCallback === 'function') {
                      ownershipDropdownOnChangeCallback(() => onOwnershipChange(OWNERSHIP_TYPE.OWNER));
                    } else {
                      onOwnershipChange(OWNERSHIP_TYPE.OWNER);
                    }
                  }}
                >
                  Owner only
                </DropdownRadioItem>
                <DropdownRadioItem
                  value={OWNERSHIP_TYPE.OWNER_AND_COLLABORATOR}
                  onClick={() => {
                    if (typeof ownershipDropdownOnChangeCallback === 'function') {
                      ownershipDropdownOnChangeCallback(() => onOwnershipChange(OWNERSHIP_TYPE.OWNER_AND_COLLABORATOR));
                    } else {
                      onOwnershipChange(OWNERSHIP_TYPE.OWNER_AND_COLLABORATOR);
                    }
                  }}
                >
                  Owner or Collaborator
                </DropdownRadioItem>
              </DropdownRadioGroup>
            </DropdownContent>
          </Dropdown>
        </Box>
      )}

      {showCompanyFilter && (
        <MultiSelectWithCheckboxes
          options={allCompanies.map(({ companyName, id }) => ({ label: companyName, value: id }))}
          selectedOptions={selectedCompanies.map(({ companyName, id }) => ({
            label: companyName,
            value: id,
          }))}
          onChange={(selected) => {
            dispatch(setSelectedCompanies(selected.map(({ label, value }) => ({ companyName: label, id: value }))));
          }}
          placeholder="Select companies"
          label="Companies"
          outsideClickToggle={outsideClickToggle}
        />
      )}
      {showLocationFilter && (
        <MultiSelectWithCheckboxes
          options={allLocations.map((locationName) => ({ label: locationName, value: locationName }))}
          selectedOptions={selectedLocations.map((locationName) => ({
            label: locationName,
            value: locationName,
          }))}
          onChange={(selected) => {
            dispatch(setSelectedLocations(selected.map(({ value }) => value)));
          }}
          placeholder="Select locations"
          label="Locations"
          outsideClickToggle={outsideClickToggle}
        />
      )}
    </Flex>
  );
};

export { MetaFilters };
